// src/components/SubscriptionManagement.jsx
import React, { useState, useEffect } from 'react';
import { collection, addDoc, getDocs, doc, updateDoc, deleteDoc, query, orderBy, serverTimestamp } from 'firebase/firestore';
import { Package, PlusCircle, Edit, Trash2, CheckCircle, XCircle, DollarSign, Clock, List, Info, TrendingUp, CircleDotDashed, Loader2 } from 'lucide-react';

const SubscriptionManagement = ({ db, appId, setMessage, setError }) => {
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [currentPackage, setCurrentPackage] = useState({
    id: null,
    name: '',
    price: '',
    currency: 'XOF', // Valeur par défaut
    duration: '',
    durationInMonths: '',
    description: '',
    features: [],
    isActive: true,
  });
  const [featureInput, setFeatureInput] = useState(''); // Pour l'ajout de fonctionnalités

  // Référence à la collection des packages
  const packagesCollectionRef = collection(db, `artifacts/${appId}/packages`);

  // Fonction pour récupérer tous les forfaits
  const fetchPackages = async () => {
    setLoading(true);
    try {
      const q = query(packagesCollectionRef, orderBy('name'));
      const querySnapshot = await getDocs(q);
      const packagesData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setPackages(packagesData);
    } catch (err) {
      console.error("Erreur lors de la récupération des forfaits:", err);
      setError("Erreur lors du chargement des forfaits d'abonnement.");
    } finally {
      setLoading(false);
    }
  };

  // Chargement des forfaits au montage du composant
  useEffect(() => {
    if (db && appId) { // S'assurer que db et appId sont disponibles
      fetchPackages();
    }
  }, [db, appId]); // Dépendances: re-fetch si db ou appId changent

  // Fonction pour ajouter une fonctionnalité à la liste
  const handleAddFeature = () => {
    if (featureInput.trim() !== '') {
      setCurrentPackage(prev => ({
        ...prev,
        features: [...prev.features, featureInput.trim()]
      }));
      setFeatureInput('');
    }
  };

  // Fonction pour supprimer une fonctionnalité de la liste
  const handleRemoveFeature = (indexToRemove) => {
    setCurrentPackage(prev => ({
      ...prev,
      features: prev.features.filter((_, index) => index !== indexToRemove)
    }));
  };

  // Fonction pour soumettre le formulaire (ajouter ou modifier)
  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage('');
    setError('');

    // Validation des champs obligatoires
    if (!currentPackage.name.trim() || !currentPackage.price || !currentPackage.duration.trim() || !currentPackage.durationInMonths) {
      setError("Veuillez remplir tous les champs obligatoires (Nom, Prix, Durée, Durée en mois).");
      return;
    }

    try {
      if (isEditing) {
        // Mode édition: mise à jour d'un forfait existant
        const packageDocRef = doc(packagesCollectionRef, currentPackage.id);
        await updateDoc(packageDocRef, {
          name: currentPackage.name.trim(),
          price: Number(currentPackage.price),
          currency: currentPackage.currency,
          duration: currentPackage.duration.trim(),
          durationInMonths: Number(currentPackage.durationInMonths),
          description: currentPackage.description.trim(),
          features: currentPackage.features,
          isActive: currentPackage.isActive,
          lastUpdatedAt: serverTimestamp(), // Mettre à jour la date de modification
        });
        setMessage("Forfait mis à jour avec succès !");
      } else {
        // Mode ajout: création d'un nouveau forfait
        await addDoc(packagesCollectionRef, {
          name: currentPackage.name.trim(),
          price: Number(currentPackage.price),
          currency: currentPackage.currency,
          duration: currentPackage.duration.trim(),
          durationInMonths: Number(currentPackage.durationInMonths),
          description: currentPackage.description.trim(),
          features: currentPackage.features,
          isActive: currentPackage.isActive,
          createdAt: serverTimestamp(), // Définir la date de création
          lastUpdatedAt: serverTimestamp(),
        });
        setMessage("Nouveau forfait ajouté avec succès !");
      }
      resetForm(); // Réinitialiser le formulaire
      fetchPackages(); // Recharger la liste des forfaits
    } catch (err) {
      console.error("Erreur lors de l'enregistrement du forfait:", err);
      setError("Erreur lors de l'enregistrement du forfait. Veuillez réessayer.");
    }
  };

  // Fonction pour préparer le formulaire pour l'édition
  const handleEditClick = (pkg) => {
    setIsEditing(true);
    setCurrentPackage({
      id: pkg.id,
      name: pkg.name,
      price: pkg.price,
      currency: pkg.currency,
      duration: pkg.duration,
      durationInMonths: pkg.durationInMonths,
      description: pkg.description,
      features: pkg.features || [], // Assurer que c'est un tableau
      isActive: pkg.isActive,
    });
    // Scroll to form if needed
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Fonction pour supprimer un forfait
  const handleDeleteClick = async (packageId, packageName) => {
    if (window.confirm(`Êtes-vous sûr de vouloir supprimer le forfait "${packageName}" ? Cette action est irréversible.`)) {
      try {
        await deleteDoc(doc(packagesCollectionRef, packageId));
        setMessage("Forfait supprimé avec succès !");
        fetchPackages(); // Recharger la liste
      } catch (err) {
        console.error("Erreur lors de la suppression du forfait:", err);
        setError("Erreur lors de la suppression du forfait. Veuillez réessayer.");
      }
    }
  };

  // Fonction pour réinitialiser le formulaire
  const resetForm = () => {
    setIsEditing(false);
    setCurrentPackage({
      id: null,
      name: '',
      price: '',
      currency: 'XOF',
      duration: '',
      durationInMonths: '',
      description: '',
      features: [],
      isActive: true,
    });
    setFeatureInput('');
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-48">
        <Loader2 className="animate-spin text-blue-600" size={48} />
        <p className="ml-4 text-xl text-gray-700">Chargement des forfaits...</p>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 bg-gray-50 rounded-lg shadow-lg">
      <h2 className="text-3xl font-bold text-gray-800 mb-6 flex items-center">
        <Package size={28} className="mr-3 text-blue-600" /> Gestion des Forfaits d'Abonnement
      </h2>

      {/* Formulaire d'ajout/édition de forfait */}
      <div className="bg-white p-6 rounded-lg shadow-md mb-8">
        <h3 className="text-2xl font-semibold text-gray-700 mb-4 flex items-center">
          {isEditing ? <Edit size={24} className="mr-2 text-green-500" /> : <PlusCircle size={24} className="mr-2 text-blue-500" />}
          {isEditing ? `Modifier le forfait: ${currentPackage.name}` : "Ajouter un Nouveau Forfait"}
        </h3>
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Nom du forfait */}
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">Nom du forfait <span className="text-red-500">*</span></label>
            <input
              type="text"
              id="name"
              value={currentPackage.name}
              onChange={(e) => setCurrentPackage({ ...currentPackage, name: e.target.value })}
              className="mt-1 block w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
              placeholder="Ex: Forfait Annuel Étudiant"
              required
            />
          </div>

          {/* Prix et Devise */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="price" className="block text-sm font-medium text-gray-700 mb-1">Prix <span className="text-red-500">*</span></label>
              <div className="relative mt-1">
                <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-gray-400"><DollarSign size={20} /></span>
                <input
                  type="number"
                  id="price"
                  value={currentPackage.price}
                  onChange={(e) => setCurrentPackage({ ...currentPackage, price: e.target.value })}
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Ex: 50000"
                  required
                />
              </div>
            </div>
            <div>
              <label htmlFor="currency" className="block text-sm font-medium text-gray-700 mb-1">Devise</label>
              <input
                type="text"
                id="currency"
                value={currentPackage.currency}
                onChange={(e) => setCurrentPackage({ ...currentPackage, currency: e.target.value })}
                className="mt-1 block w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                placeholder="Ex: XOF"
                required
              />
            </div>
          </div>

          {/* Durée et Durée en mois */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="duration" className="block text-sm font-medium text-gray-700 mb-1">Durée <span className="text-red-500">*</span></label>
              <div className="relative mt-1">
                 <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-gray-400"><Clock size={20} /></span>
                <input
                  type="text"
                  id="duration"
                  value={currentPackage.duration}
                  onChange={(e) => setCurrentPackage({ ...currentPackage, duration: e.target.value })}
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Ex: Annuel, Mensuel"
                  required
                />
              </div>
            </div>
            <div>
              <label htmlFor="durationInMonths" className="block text-sm font-medium text-gray-700 mb-1">Durée en mois <span className="text-red-500">*</span></label>
              <input
                type="number"
                id="durationInMonths"
                value={currentPackage.durationInMonths}
                onChange={(e) => setCurrentPackage({ ...currentPackage, durationInMonths: e.target.value })}
                className="mt-1 block w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                placeholder="Ex: 12 (pour Annuel)"
                required
              />
            </div>
          </div>

          {/* Description */}
          <div>
            <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea
              id="description"
              value={currentPackage.description}
              onChange={(e) => setCurrentPackage({ ...currentPackage, description: e.target.value })}
              rows="3"
              className="mt-1 block w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
              placeholder="Description détaillée du forfait..."
            ></textarea>
          </div>

          {/* Fonctionnalités */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Fonctionnalités</label>
            <div className="flex space-x-2 mb-2">
              <div className="relative flex-grow">
                 <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-gray-400"><List size={20} /></span>
                <input
                  type="text"
                  value={featureInput}
                  onChange={(e) => setFeatureInput(e.target.value)}
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Ajouter une fonctionnalité (Ex: Support Premium)"
                />
              </div>
              <button
                type="button"
                onClick={handleAddFeature}
                className="bg-gray-200 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-300 transition duration-200"
              >
                Ajouter
              </button>
            </div>
            <ul className="list-disc list-inside text-sm text-gray-600">
              {currentPackage.features.map((feature, index) => (
                <li key={index} className="flex items-center justify-between bg-gray-50 p-2 rounded-md mb-1">
                  <span><Info size={16} className="inline mr-2 text-blue-500" />{feature}</span>
                  <button type="button" onClick={() => handleRemoveFeature(index)} className="text-red-500 hover:text-red-700">
                    <XCircle size={16} />
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Statut Actif */}
          <div className="flex items-center">
            <input
              type="checkbox"
              id="isActive"
              checked={currentPackage.isActive}
              onChange={(e) => setCurrentPackage({ ...currentPackage, isActive: e.target.checked })}
              className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
            <label htmlFor="isActive" className="ml-2 block text-sm font-medium text-gray-700">Forfait Actif</label>
          </div>

          {/* Boutons d'action du formulaire */}
          <div className="flex space-x-4">
            <button
              type="submit"
              className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-md font-semibold text-lg hover:bg-blue-700 transition duration-200"
            >
              {isEditing ? 'Mettre à jour le forfait' : 'Ajouter le forfait'}
            </button>
            {isEditing && (
              <button
                type="button"
                onClick={resetForm}
                className="flex-1 bg-gray-300 text-gray-800 py-2 px-4 rounded-md font-semibold text-lg hover:bg-gray-400 transition duration-200"
              >
                Annuler l'édition
              </button>
            )}
          </div>
        </form>
      </div>

      {/* Liste des forfaits existants */}
      <div className="bg-white p-6 rounded-lg shadow-md">
        <h3 className="text-2xl font-semibold text-gray-700 mb-4 flex items-center">
          <List size={24} className="mr-2 text-purple-500" /> Forfaits Actuels ({packages.length})
        </h3>
        {packages.length === 0 ? (
          <p className="text-gray-600 italic">Aucun forfait d'abonnement trouvé pour le moment.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {packages.map((pkg) => (
              <div key={pkg.id} className="bg-gray-100 p-4 rounded-lg shadow flex flex-col justify-between border border-gray-200">
                <div>
                  <h4 className="text-xl font-bold text-gray-800 mb-2 flex items-center">
                    <TrendingUp size={20} className="mr-2 text-green-600" /> {pkg.name}
                  </h4>
                  <p className="text-2xl font-semibold text-blue-600 mb-2">{pkg.price} {pkg.currency}</p>
                  <p className="text-gray-700 mb-1 flex items-center"><Clock size={16} className="mr-2 text-gray-500" /> Durée: {pkg.duration} ({pkg.durationInMonths} mois)</p>
                  {pkg.description && <p className="text-gray-600 text-sm italic mb-2">{pkg.description}</p>}
                  {pkg.features && pkg.features.length > 0 && (
                    <ul className="list-disc list-inside text-sm text-gray-700 mt-2">
                      <p className="font-semibold text-gray-700 flex items-center mb-1"><CircleDotDashed size={16} className="mr-2 text-indigo-500" /> Fonctionnalités:</p>
                      {pkg.features.map((feature, idx) => (
                        <li key={idx}>{feature}</li>
                      ))}
                    </ul>
                  )}
                  <p className={`text-sm mt-2 font-medium flex items-center ${pkg.isActive ? 'text-green-600' : 'text-red-600'}`}>
                    {pkg.isActive ? <CheckCircle size={16} className="mr-2" /> : <XCircle size={16} className="mr-2" />}
                    Statut: {pkg.isActive ? 'Actif' : 'Inactif'}
                  </p>
                </div>
                <div className="mt-4 flex space-x-2">
                  <button
                    onClick={() => handleEditClick(pkg)}
                    className="flex-1 bg-yellow-500 text-white py-2 px-3 rounded-md hover:bg-yellow-600 transition duration-200 flex items-center justify-center text-sm"
                  >
                    <Edit size={16} className="mr-2" /> Modifier
                  </button>
                  <button
                    onClick={() => handleDeleteClick(pkg.id, pkg.name)}
                    className="flex-1 bg-red-600 text-white py-2 px-3 rounded-md hover:bg-red-700 transition duration-200 flex items-center justify-center text-sm"
                  >
                    <Trash2 size={16} className="mr-2" /> Supprimer
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default SubscriptionManagement;